import React, {useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {
    AutocompleteChangeDetails,
    AutocompleteChangeReason,
    AutocompleteProps,
    AutocompleteValue,
    createFilterOptions,
    FilterOptionsState,
    IconButton,
    InputAdornment,
    TextFieldProps,
    Tooltip,
} from "@mui/material";
import {SettingsOutlined} from "@mui/icons-material";
import {CancelToken} from "axios";
import AutocompleteBase from "../autocomplete-base";
import Api from "../../../../../core/services/api/api";
import useAccessLevels from "../../../../hooks/use-access-levels";
import useIsMounted from "../../../../hooks/use-is-mounted";

export interface CompetitorOption {
    id?: number | null;
    title: string;
    isPublic?: boolean;
    isNew?: boolean;
}

interface CompetitorAutocompleteProps<M extends boolean = false> extends Omit<AutocompleteProps<CompetitorOption, M, false, boolean>, 'renderInput' | 'options'> {
    textFieldProps?: Partial<TextFieldProps>;
    form?: boolean;
    name?: string;
    required?: boolean;
    onManageClick?: () => void;
    dependencies?: readonly any[];

    [key: string]: any;
}

/**
 * Autocomplete over GET CleanAF/Competitors/List. Supports freeSolo create when the operator can manage competitors.
 */
const CompetitorAutocomplete = <M extends boolean = false, >({
                                                                 textFieldProps = {},
                                                                 form = false,
                                                                 name = undefined,
                                                                 required = false,
                                                                 onManageClick,
                                                                 dependencies = [],
                                                                 ...props
                                                             }: CompetitorAutocompleteProps<M>) => {
    const {t} = useTranslation('components', {keyPrefix: 'app-specific.competitor-autocomplete'});
    const operatorAccessLevels = useAccessLevels();
    const isMounted = useIsMounted();
    const canManage = operatorAccessLevels.packages.lookup.manageCompetitors;
    const canOpenManage = canManage || operatorAccessLevels.packages.lookup.viewCompetitors;
    const freeSolo = canManage && (props.freeSolo !== false);

    const defaultFilterOptions = useMemo(() => createFilterOptions<CompetitorOption>(), []);

    /**
     * Loads the full competitor list (unpaginated).
     */
    const getOptions = useCallback(async (cancelToken: CancelToken) => {
        const response = await Api.listCompetitors(cancelToken);
        if (!isMounted())
            return [];
        return (response?.data ?? []) as CompetitorOption[];
    }, [isMounted]);

    /**
     * Creates a competitor from a freeSolo title, then returns the created entity.
     */
    const createCompetitor = useCallback(async (title: string) => {
        const response = await Api.addCompetitor({title: title.trim()});
        if (!isMounted())
            return null;
        return response?.resultFlag ? (response.data as CompetitorOption) : null;
    }, [isMounted]);

    const isOptionEqualToValue = useCallback((option: CompetitorOption, value: CompetitorOption) => {
        if (option?.isNew || value?.isNew)
            return option?.title === value?.title;
        if (option?.id != null && value?.id != null)
            return option.id === value.id;
        return option?.title === value?.title;
    }, []);

    const getOptionLabel = useCallback((option: CompetitorOption | string) => (
        typeof option === 'string' ? option : option?.title ?? ''
    ), []);

    const getOptionKey = useCallback((option: CompetitorOption | string) => (
        typeof option === 'string' ? option : (option?.id != null ? String(option.id) : `new-${option?.title ?? ''}`)
    ), []);

    /**
     * Injects a "Create {title}" option when the typed value is not an existing competitor.
     */
    const filterOptions = useCallback((options: CompetitorOption[], state: FilterOptionsState<CompetitorOption>) => {
        const filtered = defaultFilterOptions(options, state);
        const inputValue = state.inputValue.trim();
        if (
            freeSolo
            && inputValue
            && !filtered.some((option) => option.title?.toLowerCase() === inputValue.toLowerCase())
        ) {
            filtered.push({
                id: null,
                title: inputValue,
                isNew: true,
            });
        }
        return filtered;
    }, [defaultFilterOptions, freeSolo]);

    /**
     * Creates a competitor when a freeSolo / isNew option is selected, then forwards the entity to onChange.
     */
    const onChange = useCallback(async (
        event: React.SyntheticEvent,
        value: AutocompleteValue<CompetitorOption, M, false, boolean>,
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<CompetitorOption>,
    ) => {
        let nextValue = value;
        const raw = Array.isArray(value) ? value.find((entry) => typeof entry === 'string' || entry?.isNew) : value;
        const title = typeof raw === 'string' ? raw.trim() : (raw as CompetitorOption)?.isNew ? (raw as CompetitorOption).title?.trim() : '';
        if (title && canManage) {
            const created = await createCompetitor(title);
            if (!created) {
                return;
            }
            nextValue = (Array.isArray(value)
                ? value.map((entry) => (
                    (typeof entry === 'string' && entry.trim() === title) || (entry as CompetitorOption)?.isNew
                        ? created
                        : entry
                ))
                : created) as AutocompleteValue<CompetitorOption, M, false, boolean>;
        }
        props.onChange?.(event, nextValue, reason, details);
    }, [canManage, createCompetitor, props]);

    const defaultOptions = useMemo(() => [] as CompetitorOption[], []);

    const mergedTextFieldProps = useMemo(() => ({
        ...textFieldProps,
        InputProps: {
            ...textFieldProps.InputProps,
            endAdornment: (
                <>
                    {
                        canOpenManage && onManageClick &&
                        <InputAdornment position="end">
                            <Tooltip title={t(t => t.manage)}>
                                <IconButton
                                    size="small"
                                    aria-label={t(t => t.manage)}
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onManageClick();
                                    }}
                                >
                                    <SettingsOutlined fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                        </InputAdornment>
                    }
                    {textFieldProps.InputProps?.endAdornment}
                </>
            ),
        },
    }), [canOpenManage, onManageClick, t, textFieldProps]);

    return (
        <AutocompleteBase<CompetitorOption, M, false, boolean>
            keywordsBased={false}
            canSearchEmptyKeywords
            getOptions={getOptions}
            timeout={300}
            textFieldProps={mergedTextFieldProps}
            form={form}
            name={name}
            required={required}
            label={props.label ?? t(t => t.label)}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            getOptionKey={getOptionKey}
            options={defaultOptions}
            dependencies={dependencies}
            freeSolo={freeSolo}
            selectOnFocus
            handleHomeEndKeys
            clearOnBlur={freeSolo}
            filterOptions={filterOptions}
            {...props}
            {...(!form ? {onChange} : {})}
            renderOption={(optionProps, option) => {
                const {key, ...rest} = optionProps as typeof optionProps & { key?: string };
                return (
                    <li key={key} {...rest}>
                        {option.isNew ? t(t => t.create, {title: option.title}) : option.title}
                    </li>
                );
            }}
        />
    );
};

export default CompetitorAutocomplete;
